import { useRouter } from 'next/router'
import React, { useMemo } from 'react'
import NextLink from 'next/link'

import type { ButtonProps } from '@wartek-id/button-v1'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text'
import { Shimmer } from '@wartek-id/shimmer'
import { Badge } from '@wartek-id/badge'
import { Link } from '@wartek-id/link'

import {
  allSelectionDocumentTypes,
  useQueryAPIGetKSInvitationDocuments,
  useQueryAPIGetKSSelectionCandidate,
  useQueryAPIGetKSSelectionProposal,
  useQueryApiGetKSSelectionResult,
  useQueryApiGetLMSCertificate,
  useQueryAPIGetVerificationDocuments,
} from 'api/bcks/useAPIBCKSInvitation'
import { useAPIGetKepalaSekolahEligibility } from 'api/bcks/useAPIBCKSQualification'
import VerticalProgressItem from 'app/BCKSPage/blocks/VerticalProgressItem'
import { getDayToDeadlineState } from 'app/BCKSPage/utils/date'
import useKSPSTrackEvent from 'app/BCKSPage/hooks/useKSPSTrackEvent'
import { useAPIPhicosGetTokenUploadPhoto } from 'api/bcks/useAPIExternal'
import { getIsSelectionStatePassedV2 as getIsSelectionStatePassed } from 'app/BCKSPage/utils/selectionState'
import Announcement from 'app/BCKSPage/components/Announcement'
import {
  SELECTION_STATES_ADMINISTRASI,
  SELECTION_STATES_PELATIHAN,
  SELECTION_STATES_PEMBUKAAN,
  URL_GFORM_SUBMIT_DOCS_GARUDA,
  PROVINCE_NAME_GARUDA,
  USER_IDS_SPECIAL_CASE_SUBSTANCE,
} from 'app/BCKSPage/constants'
import { useAuth } from 'utils/auth'
import { formatDate } from 'utils/datetime'

import type { PropsAnnouncement } from 'app/BCKSPage/components/Announcement'
import type { ReactNode } from 'react'
import type { SelectionCandidateState } from 'api/bcks/useAPIBCKSInvitation'

interface AnnouncementState
  extends Pick<PropsAnnouncement, 'type' | 'title' | 'description'> {
  action?: ReactNode
}

const getAnnouncementState = (
  selectionCandidateState: SelectionCandidateState,
  isSpecialCaseSubstance: boolean
): AnnouncementState => {
  switch (selectionCandidateState) {
    case 'substanceInProgress':
      return {
        type: 'success',
        title: 'Anda lolos Seleksi Administrasi',
        description:
          'Anda mendapat undangan untuk mengikuti tahap berikutnya. Hubungi Dinas untuk selengkapnya.',
      }
    case 'substanceFinished':
      return {
        type: 'success',
        title: isSpecialCaseSubstance ? '' : 'Anda lulus Seleksi Substansi',
        description: isSpecialCaseSubstance
          ? 'Berdasarkan tambahan kuota oleh Dinas, Anda akan dikelaskan dalam diklat pelatihan.'
          : 'Anda akan dikelaskan dalam diklat pelatihan.',
      }
    case 'diklatPending':
    case 'diklatInProgress':
      return {
        type: 'success',
        title: 'Anda sudah dikelaskan dalam pelatihan',
        description: 'Silakan cek melalui tombol di bawah ini.',
        action: (
          <a href={LMS_URL} target="_blank" rel="noreferrer">
            <Button fullWidth>Cek kelas pelatihan</Button>
          </a>
        ),
      }
    case 'adminFailed':
    case 'adminCanceled':
    case 'substanceFailed':
    case 'diklatFailed':
      return {
        type: 'critical',
        title: 'Maaf, Anda tidak dilanjutkan ke tahap berikutnya',
      }
    case 'diklatFinished':
      return {
        type: 'success',
        title: 'Anda lolos Pelatihan',
        description: 'Silakan unggah Surat Keterangan Seminar.',
        action: (
          <a href={LMS_URL} target="_blank" rel="noreferrer">
            <Button fullWidth>Unggah dokumen di LMS</Button>
          </a>
        ),
      }
    case 'adminFinished':
      return {
        type: 'default',
        title: 'Mohon tunggu informasi selanjutnya',
        description: 'Hubungi Dinas untuk selengkapnya.',
      }
  }

  return undefined
}

const getNapzaState = (selectionCandidateState: SelectionCandidateState) => {
  switch (selectionCandidateState) {
    case 'napzaInvitation':
      return {
        desc: 'Dokumen NAPZA Anda diperlukan oleh Dinas untuk menyelesaikan proses seleksi.',
      }
    case 'napzaVerification':
      return {
        desc: 'Dokumen NAPZA Anda diperlukan oleh Dinas untuk menyelesaikan proses seleksi.',
        badgeText: 'Berkas sedang dicek oleh Dinas',
        badgeColor: '!bg-[#98DAFB]',
      }
    case 'napzaFinished':
      return {
        desc: 'Dokumen NAPZA Anda sudah diverifikasi oleh Dinas. Mohon tunggu informasi selanjutnya.',
        badgeText: 'Sudah diverifikasi',
        badgeColor: '!bg-[#E2F5E0]',
      }
    case 'napzaFailed':
      return {
        desc: 'Dokumen NAPZA Anda sudah diverifikasi oleh Dinas.',
        badgeText: 'Verifikasi gagal',
        badgeColor: '!bg-[#FFEDCE]',
      }
  }
}

const SELECTION_STEP_1_DOC_REQUIREMENTS = [
  {
    description:
      'Hasil Penilaian Kinerja (diterbitkan dalam 2 tahun terakhir dan dapat dihitung dari sebelum menjabat sebagai guru PPPK)',
    subRequirements: [],
  },
  {
    description:
      'SK Pengalaman Manajerial (diterbitkan dalam 2 tahun terakhir)',
    subRequirements: ['Khusus PPPK lampirkan juga bukti pengalaman kerja'],
  },
  {
    description:
      'SK Bebas Hukuman Disiplin (diterbitkan dalam 2 tahun terakhir)',
    subRequirements: [],
  },
  {
    description: 'SKCK (diterbitkan dalam 6 bulan terakhir)',
    subRequirements: [],
  },
  {
    description: 'Pakta Integritas',
    subRequirements: [],
  },
]

const SELECTION_STEP_1_ADDITIONAL_DOCS_GARUDA = [
  {
    description: 'Surat Persetujuan mengikuti proses dari Pejabat',
    subRequirements: [],
  },
  {
    description: 'Surat Lamaran',
    subRequirements: [],
  },
  {
    description: 'Surat Pernyataan bersedia tinggal di asrama',
    subRequirements: [],
  },
  {
    description: 'CV',
    subRequirements: [],
  },
  {
    description: 'Salinan Ijazah dan Transkrip Nilai',
    subRequirements: [],
  },
  {
    description:
      'Sertifikat Kemampuan Bahasa Inggris sebagai persyaratan administrasi',
    subRequirements: [],
  },
]

const NUM_OF_REQUIRED_DOCS = 5
const LMS_URL = process.env.NEXT_PUBLIC_LMS_DIKLAT_URL + '?source=bcks'

const LinkBCKSGuide = (buttonProps: ButtonProps) => {
  const { KSPSTrackEvent } = useKSPSTrackEvent()

  return (
    <NextLink href="/bcks/invitation/guide" passHref>
      <Button
        fullWidth
        color="black"
        onClick={() => {
          KSPSTrackEvent({
            event_name: 'click_ks_selection_invitation_file_description',
          })
        }}
        {...buttonProps}
      >
        Baca penjelasan tiap berkas
      </Button>
    </NextLink>
  )
}

const LinkAdditionalDocsGaruda = () => {
  return (
    <Link
      href={URL_GFORM_SUBMIT_DOCS_GARUDA}
      target="_blank"
      className="inline-block !no-underline w-full !p-0"
    >
      <Button variant="solid" color="white" fullWidth size="sm">
        Unggah berkas Sekolah Unggul Garuda
      </Button>
    </Link>
  )
}

export const SelectionStepsV2 = ({ staticView = false }) => {
  const { session } = useAuth()
  const router = useRouter()
  const { KSPSTrackEvent } = useKSPSTrackEvent()

  const { data: profile, isLoading: isLoadingProfile } =
    useAPIGetKepalaSekolahEligibility()

  const {
    data: candidate,
    isError: isErrorGetKSSelectionCandidate,
    isLoading: isLoadingSelectionState,
  } = useQueryAPIGetKSSelectionCandidate()
  const { data: proposalData } = useQueryAPIGetKSSelectionProposal(
    {
      enabled: isErrorGetKSSelectionCandidate, // Check for KS proposal data only if self candidate data returns error
      staleTime: 60 * 60 * 1000, // Stale after 60min, force to refetch upon mounting
      useErrorBoundary: false, // Prevent whole page error
    },
    { version: 2 }
  )

  const { selectionCandidateState, selectionState, reason } = candidate ?? {}

  const { data: documentState, isLoading: isLoadingDocuments } =
    useQueryAPIGetKSInvitationDocuments(candidate?.selectionId || '')

  const { data: result } = useQueryApiGetKSSelectionResult()

  const {
    data: verificationDocuments,
    isLoading: isLoadingVerificationDocuments,
  } = useQueryAPIGetVerificationDocuments(candidate?.selectionId, {
    enabled: selectionCandidateState === 'documentVerification',
  })
  const deadlineState = getDayToDeadlineState(candidate?.deadlineDate)

  const {
    data: photoTokenData,
    refetch: getTokenUploadPhoto,
    isLoading: isLoadingToken,
    isRefetching: isRefetchingToken,
  } = useAPIPhicosGetTokenUploadPhoto()
  const isGettingToken = isLoadingToken || isRefetchingToken

  // Pre-fetch token on substanceInProgress, auto-refresh every 55 min
  React.useEffect(() => {
    if (selectionCandidateState === 'substanceInProgress') {
      getTokenUploadPhoto()
      const interval = setInterval(getTokenUploadPhoto, 55 * 60 * 1000)
      return () => clearInterval(interval)
    }
  }, [selectionCandidateState, getTokenUploadPhoto])

  const { data: lmsCertificate } = useQueryApiGetLMSCertificate(
    {
      selectionId: candidate?.selectionId,
      candidateId: session?.user.id,
    },
    {
      enabled: ['diklatCertificateIssued', 'napzaRegularInvitation'].includes(
        selectionCandidateState
      ),
    }
  )

  const isLoading =
    isLoadingSelectionState ||
    isLoadingDocuments ||
    isLoadingVerificationDocuments

  const isOnStepUploadDocs =
    [
      'documentSubmission',
      'documentVerification',
      'documentVerificationFailed',
    ].includes(selectionCandidateState) ||
    selectionState === 'documentVerification'

  const isCandidateFailedAdministration = [
    'adminFailed',
    'adminCanceled',
  ].includes(selectionCandidateState)

  const isProvinceGaruda =
    !isLoadingProfile &&
    profile?.currentAssignment?.provinsiSekolah?.value.toLowerCase() ===
      PROVINCE_NAME_GARUDA.toLowerCase()

  const showAdditionalDocsGarudaLink =
    isProvinceGaruda && selectionCandidateState === 'documentVerification'

  const docRequirements = isProvinceGaruda
    ? [
        ...SELECTION_STEP_1_DOC_REQUIREMENTS,
        ...SELECTION_STEP_1_ADDITIONAL_DOCS_GARUDA,
      ]
    : SELECTION_STEP_1_DOC_REQUIREMENTS

  const showUnggahBerkasButton =
    !isLoading &&
    selectionCandidateState === 'documentSubmission' &&
    deadlineState === 'withinDeadline'

  const numOfUploadedDoc = useMemo(() => {
    let count = 0
    if (documentState) {
      Object.keys(documentState).forEach((key) => {
        if (key.includes('ThumbnailUrl') && documentState[key].length > 0) {
          count++
        }
      })
    }

    return count
  }, [documentState])

  const hasRevisions = useMemo(
    () =>
      allSelectionDocumentTypes.some(
        (docType) => verificationDocuments?.[docType]?.state === 'NEED_REVISION'
      ),
    [verificationDocuments]
  )

  const handleActionButtonClick = () => {
    KSPSTrackEvent({
      event_name: 'click_ks_selection_process_upload_file',
    })

    if (numOfUploadedDoc === NUM_OF_REQUIRED_DOCS) {
      router.push('/bcks/invitation/review')
    } else {
      router.push('/bcks/invitation/collection')
    }
  }

  const handleGetRedirectionUploadPhoto = () => {
    if (photoTokenData?.token) {
      window.open(
        `${process.env.NEXT_PUBLIC_PHICOS_BASE_URL}/upload-photo/${photoTokenData.token}`
      )
    }
  }

  const shouldShowVerificationBadge =
    !staticView &&
    !hasRevisions &&
    (['documentVerification', 'documentVerificationFailed'].includes(
      selectionCandidateState
    ) ||
      (selectionCandidateState === 'adminFinished' &&
        selectionState === 'documentVerification'))

  const SELECTION_STEPS = [
    {
      title: 'Pembukaan Seleksi',
      description: 'Pendaftaran seleksi BCKS mengikuti jadwal yang ditentukan',
      isDone: getIsSelectionStatePassed(selectionCandidateState, 'invitation'),
    },
    {
      title: 'Seleksi Administrasi',
      description: (
        <>
          <div className="mt-1.5">
            {candidate &&
              ([
                ...SELECTION_STATES_PEMBUKAAN,
                ...SELECTION_STATES_ADMINISTRASI,
              ].includes(selectionCandidateState) ||
                !!proposalData) && (
                <Badge variant="informational">
                  {formatDate(
                    candidate?.startDate ?? proposalData?.selectionStartDate
                  )}{' '}
                  -{' '}
                  {formatDate(
                    candidate?.endDate ?? proposalData?.selectionEndDate
                  )}
                </Badge>
              )}
          </div>

          {!staticView && hasRevisions && (
            <div className="mt-1.5">
              <Badge variant="warning">Perlu diperbaiki</Badge>
            </div>
          )}

          {shouldShowVerificationBadge && (
            <Badge variant="informational" className="mt-1.5">
              Berkas sedang dicek oleh Dinas
            </Badge>
          )}

          <Text variant="body-sm" color="subdued" className="mt-1.5">
            Tahap pertama adalah Seleksi Administrasi. Kumpulkan berkas berikut
            yang asli (<strong>bukan fotokopi</strong>), format{' '}
            <strong>PDF</strong>, ukuran <strong>maks. 5 MB</strong>:
          </Text>

          <ol className="list-decimal list-outside text-[13px] text-subdued pl-5 mb-5">
            {docRequirements.map(({ description, subRequirements }, index) => (
              <li key={`text-${index}`}>
                <Text variant="body-sm" color="subdued">
                  {description}
                </Text>
                <ol className="pl-5 list-outside list-[lower-alpha]">
                  {subRequirements.map((req, reqIndex) => (
                    <li key={`sub-${reqIndex}`}>
                      <Text variant="body-sm" color="subdued">
                        {req}
                      </Text>
                    </li>
                  ))}
                </ol>
              </li>
            ))}
          </ol>

          {!staticView && isOnStepUploadDocs && (
            <Text variant="body-sm" color="subdued" className="mt-3">
              Dinas setempat akan melakukan verifikasi dan validasi berkas.
            </Text>
          )}

          {!staticView && (
            <div className="mt-1.5">
              {isLoading ? (
                <Shimmer height={24} className="!w-full" />
              ) : (
                <div className="flex flex-col gap-3">
                  {showAdditionalDocsGarudaLink && (
                    <div>
                      <Text variant="body-sm" className="uppercase !font-bold">
                        Informasi
                      </Text>{' '}
                      <Text variant="body-sm" className="mb-2">
                        Jika Anda yang belum mengunggah dokumen khusus{' '}
                        <strong>Sekolah Unggul Garuda</strong>, mohon unggah
                        melalui tombol di bawah ini:
                      </Text>
                      <LinkAdditionalDocsGaruda />
                    </div>
                  )}

                  {hasRevisions && (
                    <NextLink href="/bcks/invitation/revision" passHref>
                      <Button fullWidth color="black">
                        Perbaiki berkas
                      </Button>
                    </NextLink>
                  )}
                </div>
              )}
            </div>
          )}

          {!staticView && showUnggahBerkasButton && (
            <div className="mt-1.5">
              <Button fullWidth color="blue" onClick={handleActionButtonClick}>
                {isLoading
                  ? ''
                  : numOfUploadedDoc > 0
                  ? 'Lanjutkan draf'
                  : 'Unggah berkas'}
              </Button>
            </div>
          )}

          {(staticView ||
            [
              'notInvited',
              'notInvitedGuru',
              'notInvitedKS',
              'invitation',
              'documentSubmission',
            ].includes(selectionCandidateState)) && (
            <div className="mt-1.5">
              <LinkBCKSGuide
                color={
                  selectionCandidateState === 'documentSubmission'
                    ? 'white'
                    : 'black'
                }
              />
            </div>
          )}
        </>
      ),
      isActive: SELECTION_STATES_ADMINISTRASI.slice(0, 2).includes(
        selectionCandidateState
      ),
      isDone: getIsSelectionStatePassed(
        selectionCandidateState,
        'documentVerificationFailed'
      ),
    },
    {
      title: 'Pengumuman Seleksi Administrasi',
      description: 'Hasil Seleksi Administrasi',
      isActive: ['adminFinished', 'substanceInProgress'].includes(
        selectionCandidateState
      ),
      isDone: getIsSelectionStatePassed(
        selectionCandidateState,
        'substanceInProgress'
      ),
      isFailed: isCandidateFailedAdministration,
    },
    {
      title: 'Seleksi Substansi',
      description: 'Seleksi dilaksanakan secara luring.',
      isDone: getIsSelectionStatePassed(
        selectionCandidateState,
        'substanceInProgress'
      ),
    },
    {
      title: 'Pengumuman Seleksi Substansi',
      description: 'Hasil Seleksi Substansi',
      isActive: selectionCandidateState === 'substanceFinished',
      isDone: getIsSelectionStatePassed(
        selectionCandidateState,
        'substanceFinished'
      ),
      isFailed: selectionCandidateState === 'substanceFailed',
    },
    {
      title: 'Pelatihan',
      description: (
        <div className="w-full">
          <Text variant="body-sm" color="subdued" className="my-1.5">
            Seleksi dilaksanakan secara luring.
          </Text>
          {!staticView &&
            ['diklatPending', 'diklatInProgress'].includes(
              selectionCandidateState
            ) && (
              <a href={LMS_URL} target="_blank" rel="noopener noreferrer">
                <Button fullWidth>Cek kelas pelatihan</Button>
              </a>
            )}
        </div>
      ),
      isActive: ['diklatPending', 'diklatInProgress'].includes(
        selectionCandidateState
      ),
      isDone: getIsSelectionStatePassed(
        selectionCandidateState,
        'diklatInProgress'
      ),
    },
    {
      title: 'Pengumuman Hasil Pelatihan',
      description: (
        <div className="w-full">
          <Text variant="body-sm" color="subdued" className="my-1.5">
            Hasil pelatihan BCKS
          </Text>
          {!staticView && selectionCandidateState === 'diklatFinished' && (
            <a href={LMS_URL} target="_blank" rel="noopener noreferrer">
              <Button fullWidth>Unggah dokumen di LMS</Button>
            </a>
          )}
          {!staticView &&
            selectionCandidateState === 'diklatCertificateIssued' && (
              <NextLink
                href={`/bcks/pdf-preview?url=${encodeURIComponent(
                  lmsCertificate?.fileUrl || ''
                )}&title=Sertifikat CKS`}
              >
                <Button disabled={!lmsCertificate?.fileUrl} fullWidth>
                  Lihat Sertifikat CKS
                </Button>
              </NextLink>
            )}
        </div>
      ),
      isActive: SELECTION_STATES_PELATIHAN.slice(4).includes(
        selectionCandidateState
      ),
      isDone: getIsSelectionStatePassed(
        selectionCandidateState,
        'diklatCertificateIssued'
      ),
      isFailed: selectionCandidateState === 'diklatFailed',
    },
    {
      title: 'Penugasan',
      description: (
        <div className="w-full">
          <Text variant="body-sm" color="subdued" className="my-1.5">
            Penugasan Kepala Sekolah
          </Text>
          {!staticView && (
            <div className="mt-1.5 space-y-2">
              {['napzaRegularInvitation', 'napzaInvitation'].includes(
                selectionCandidateState
              ) && (
                <NextLink href="/bcks/invitation/napza">
                  <Button fullWidth>Unggah Dokumen NAPZA</Button>
                </NextLink>
              )}
              {['napzaVerification', 'napzaRegularVerification'].includes(
                selectionCandidateState
              ) && (
                <Badge variant="informational">
                  Sedang diverifikasi oleh Dinas
                </Badge>
              )}
              {['napzaFinished', 'napzaRegularFinished'].includes(
                selectionCandidateState
              ) && <Badge variant="success">Sudah diverifikasi</Badge>}
              {['napzaFailed', 'napzaRegularFailed'].includes(
                selectionCandidateState
              ) && <Badge variant="warning">Verifikasi gagal</Badge>}
            </div>
          )}
        </div>
      ),
      isActive:
        getIsSelectionStatePassed(
          selectionCandidateState,
          'diklatCertificateIssued'
        ) &&
        !['napzaFailed', 'napzaRegularFailed'].includes(
          selectionCandidateState
        ),
      isFailed: ['napzaFailed', 'napzaRegularFailed'].includes(
        selectionCandidateState
      ),
    },
  ]

  const announcementState =
    selectionState !== 'documentVerification' &&
    getAnnouncementState(
      selectionCandidateState,
      USER_IDS_SPECIAL_CASE_SUBSTANCE.includes(session?.user.id)
    )
  const napzaState = getNapzaState(selectionCandidateState)

  return (
    <div>
      {!staticView && announcementState && (
        <div className="p-4 bg-surface-default">
          <Announcement
            type={announcementState.type}
            title={announcementState.title}
            description={announcementState.description}
          >
            {announcementState.type === 'critical' && reason && (
              <Text variant="body-sm" color="subdued">
                Alasan:
                <br />
                <ul className="my-1 list-disc pl-4">
                  {(() => {
                    try {
                      return JSON.parse(reason).map(
                        (item: string, index: number) => (
                          <li key={index}>{item}</li>
                        )
                      )
                    } catch {
                      return <li>Gagal memuat alasan.</li>
                    }
                  })()}
                </ul>
                Terima kasih atas partisipasi Anda.
              </Text>
            )}
          </Announcement>

          {selectionCandidateState === 'substanceInProgress' && (
            <div className="mt-6">
              <Button
                fullWidth
                className="mb-1.5"
                isLoading={isGettingToken}
                disabled={isGettingToken}
                onClick={handleGetRedirectionUploadPhoto}
              >
                Unggah foto peserta
              </Button>
              {result?.skFileUrl && (
                <a href={result.skFileUrl} target="_blank" rel="noreferrer">
                  <Button fullWidth color="white">
                    Unduh Berita Acara
                  </Button>
                </a>
              )}
            </div>
          )}

          {announcementState.action && (
            <div className="mt-3">{announcementState.action}</div>
          )}
        </div>
      )}

      <div className="p-3">
        {!staticView && napzaState && (
          <div className="bg-surface-default p-3 rounded flex flex-col gap-3 border border-subdued mb-6">
            <Text variant="heading-sm">Unggah Dokumen NAPZA</Text>
            <Text variant="body-sm" color="subdued">
              {napzaState.desc}
            </Text>

            {napzaState.badgeText ? (
              <Badge className={`w-fit ${napzaState.badgeColor}`}>
                {napzaState.badgeText}
              </Badge>
            ) : (
              <NextLink href="/bcks/invitation/napza" passHref>
                <Button className="w-fit" color="black">
                  Unggah Dokumen
                </Button>
              </NextLink>
            )}
          </div>
        )}

        {SELECTION_STEPS.map((step, i) => (
          <VerticalProgressItem
            key={`step-${i}`}
            step={i + 1}
            className="pb-4"
            withVerticalLine={i < SELECTION_STEPS.length - 1}
            isDone={!staticView && step.isDone}
            isActive={!staticView && step.isActive}
            isFailed={!staticView && step.isFailed}
          >
            <Text variant="heading-sm">{step.title}</Text>
            {typeof step.description === 'string' ? (
              <Text variant="body-sm" color="subdued" className="mt-1.5">
                {step.description}
              </Text>
            ) : (
              step.description
            )}
          </VerticalProgressItem>
        ))}
      </div>
    </div>
  )
}
